// Global session-expiry detection.
// The app authenticates with a Bearer token (8h sliding TTL in auth_sessions).
// When it expires, every API call starts returning 401; without central
// handling each panel just retries forever and the user has to log out
// manually. This interceptor watches all fetches and raises one app-level
// event when the session is confirmed dead.
//
// Important: a 401 from one endpoint is NOT proof the session expired - some
// endpoints proxy to Core identity and can reject a perfectly valid AIOps
// session. So before logging the user out we confirm with /auth/me, which
// returns 401 if and only if the bearer session itself is invalid.

import { resolveApiBase } from "./apiBase";

export const SESSION_EXPIRED_EVENT = "datasnare:session-expired";
export const POST_LOGIN_REDIRECT_KEY = "datasnare:post-login-redirect";

const AUTH_ENDPOINT_PATTERN = /\/auth\/(login|logout|me|password-setup|set-password)/i;
const VERIFY_COOLDOWN_MS = 5000;

let installed = false;
let verifyInFlight = false;
let lastVerifyAt = 0;

export function installSessionExpiryInterceptor() {
    if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
    installed = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
        const hadToken = Boolean(safeGetToken());
        const response = await originalFetch(input, init);
        try {
            if (response.status === 401 && hadToken) {
                const url = typeof input === "string" ? input : String(input?.url || "");
                const isApiCall = url.includes("/api/") || url.startsWith("/api");
                if (isApiCall && !AUTH_ENDPOINT_PATTERN.test(url)) {
                    void confirmSessionExpired(originalFetch);
                }
            }
        } catch {
            // Never interfere with the caller's handling of the response.
        }
        return response;
    };
}

async function confirmSessionExpired(originalFetch) {
    if (verifyInFlight || Date.now() - lastVerifyAt < VERIFY_COOLDOWN_MS) return;
    const token = safeGetToken();
    if (!token) return;
    verifyInFlight = true;
    try {
        const response = await originalFetch(`${resolveApiBase()}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        lastVerifyAt = Date.now();
        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        }
    } catch {
        // Network failure is an outage, not a session expiry; leave the user signed in.
    } finally {
        verifyInFlight = false;
    }
}

function safeGetToken() {
    try {
        return window.localStorage.getItem("token");
    } catch {
        return null;
    }
}

export function rememberPostLoginRedirect(pathWithSearch) {
    try {
        if (pathWithSearch && pathWithSearch !== "/login") {
            window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, pathWithSearch);
        }
    } catch {
        // Session storage unavailable; deep link is best-effort.
    }
}

export function consumePostLoginRedirect() {
    try {
        const value = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
        window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        return value || null;
    } catch {
        return null;
    }
}
